import Icon from "@/components/Icon";
import { useIsDashboardStackedTableRows } from "../_hooks/useIsDashboardSingleColumn";
import DashboardEventPanelHeader from "./DashboardEventPanelHeader";
import ClickableEventRow, { ROW_BORDER } from "./ClickableEventRow";
import DashboardEventDate from "./DashboardEventDate";
import DashboardTableSkeleton from "./DashboardTableSkeleton";
import type { DashboardPastEvent } from "../_utils/dashboardEventDisplay";
import { getPastEventsScopeDescription } from "../_utils/dashboardEventScopeLabels";
import { DASHBOARD_PANEL_BODY_CLASS, DASHBOARD_PANEL_SHELL_CLASS } from "../_utils/dashboardPanelStyles";

const COLUMN_COUNT = 3;
const BODY_CELL = "px-4 py-2 h-12 align-middle text-left text-body-medium text-sys-brand-on-surface";
const DATE_CELL = "px-4 py-2 h-12 align-middle text-left whitespace-nowrap";

function PastEventRow({ event }: { event: DashboardPastEvent }) {
  const isStackedTableRows = useIsDashboardStackedTableRows();

  if (isStackedTableRows) {
    return (
      <ClickableEventRow slug={event.slug} title={event.title}>
        <td colSpan={COLUMN_COUNT} className="px-4 py-3 align-top text-body-medium text-sys-brand-on-surface">
          <div className="flex flex-col gap-1">
            <DashboardEventDate date={event.date} />
            <div className="line-clamp-2">{event.title}</div>
            <span className="inline-flex items-center gap-1 pt-0.5 text-body-small text-sys-brand-on-surface-variant">
              <Icon name="group" size={16} />
              {event.registration}
            </span>
          </div>
        </td>
      </ClickableEventRow>
    );
  }

  return (
    <ClickableEventRow slug={event.slug} title={event.title}>
      <td className={DATE_CELL}>
        <DashboardEventDate date={event.date} />
      </td>
      <td className={BODY_CELL}>{event.title}</td>
      <td className={BODY_CELL}>
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
          <Icon name="group" size={16} />
          {event.registration}
        </span>
      </td>
    </ClickableEventRow>
  );
}

type PastEventsTableProps = {
  pastEvents: DashboardPastEvent[];
  scopedSectionNames?: string[];
  loading?: boolean;
  error?: Error;
  missingSectionScope?: boolean;
};

export default function PastEventsTable({
  pastEvents,
  scopedSectionNames = [],
  loading = false,
  error,
  missingSectionScope = false
}: PastEventsTableProps) {
  const scopeDescription = missingSectionScope ? null : getPastEventsScopeDescription(scopedSectionNames);

  return (
    <section data-testid="dashboard-past-events-panel" className={DASHBOARD_PANEL_SHELL_CLASS}>
      <DashboardEventPanelHeader
        title="Past Events"
        scopeDescription={scopeDescription}
        testId="past-events-scope-description"
      />
      <div className={DASHBOARD_PANEL_BODY_CLASS}>
        <table data-testid="dashboard-past-events-table" className="w-full">
          <tbody>
            {loading ? (
              <DashboardTableSkeleton columnCount={COLUMN_COUNT} />
            ) : missingSectionScope ? (
              <tr className={ROW_BORDER} data-testid="past-events-empty-scope-row">
                <td colSpan={COLUMN_COUNT} className="px-4 py-6 text-center text-body-medium text-sys-brand-error">
                  Unable to determine which sections to show events for.
                </td>
              </tr>
            ) : error ? (
              <tr className={ROW_BORDER} data-testid="past-events-error-row">
                <td colSpan={COLUMN_COUNT} className="px-4 py-6 text-center text-body-medium text-sys-brand-error">
                  Unable to load past events. Please try again later.
                </td>
              </tr>
            ) : pastEvents.length > 0 ? (
              pastEvents.map((event) => <PastEventRow key={event.id} event={event} />)
            ) : (
              <tr className={ROW_BORDER} data-testid="past-events-empty-row">
                <td
                  colSpan={COLUMN_COUNT}
                  className="px-4 py-6 text-center text-body-medium text-sys-brand-on-surface-variant"
                >
                  No past events
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
